type Row = Record<string, any>;

export const pendingClientRateMessage = 'An appropriate rate will be provided upon review of the quote changes.';
export const pendingClientTotalMessage = 'Total pending review of requested changes and updated rates.';
export const hasPendingClientRate = (row: Row) => row.rate === null || row.rate === '' || row.rate === undefined;

export const pendingClientSendWarning = 'Rows awaiting a reviewed rate will retain their entire currently saved row in this outgoing quote. New unpriced rows will not be included. Their proposals remain in request history. Enter a reviewed rate in the row to include the requested position.';

/** The existing warning continuation retains a whole saved row, never an old price on a new position. */
export function clientRequestOutgoingRows(rows: Row[], savedRows: Row[]): Row[] {
  return rows.flatMap(row => {
    if (!row.clientRatePending) return [row];
    const saved = savedRows.find(item => Number(item.id) === Number(row.id));
    return saved ? [{ ...saved, totals: saved.total, clientRatePending: false }] : [];
  });
}

export const clientPositionSelection = (row: Row, position: string): Row =>
  position === row.position ? row : { ...row, position, rate: null, total: null };

/** A client cannot price a different position with the issued position's rate. */
export function clientPositionProposal<T extends Row>(row: T, original?: Row | null): T {
  return original && row.position !== original.position
    ? { ...row, rate: null, total: null }
    : row;
}

/** Typed history projects missing numbers to zero; review the saved raw intent. */
export function clientRequestPricingRows(history: Row | null | undefined): Row[] {
  let metadata: Row = {};
  try { const parsed = JSON.parse(history?.adminNotes || '{}'); metadata = parsed && typeof parsed === 'object' ? parsed : {}; } catch { /* Legacy history. */ }
  const proposal = metadata.clientRequestProposal;
  const addedIds = metadata.clientRequestNewHistoryShiftIds ?? (history?.shiftsHistory || [])
    .filter((row: Row) => row.shiftId === null).map((row: Row) => row.id).sort((a: number, b: number) => a - b);
  return (history?.shiftsHistory || []).map((row: Row) => {
    const raw = row.shiftId != null
      ? proposal?.updatedShifts?.find((item: Row) => Number(item.id) === Number(row.shiftId))
      : proposal?.newShifts?.[addedIds.indexOf(row.id)] ??
        metadata.clientRequestNewRowsByHistoryId?.[String(row.id)];
    return raw && Object.prototype.hasOwnProperty.call(raw, 'rate') && hasPendingClientRate(raw)
      ? { ...row, rate: null, total: null, totals: null, clientRatePending: true }
      : row;
  });
}

/** Clear inherited calculation snapshots only when the pending position is applied. */
export function applyClientPositionPricing(row: Row, requested: Row | undefined, patch: Row): Row {
  return requested && hasPendingClientRate(requested) && patch.position !== undefined && patch.position !== row.position && patch.position === requested.position
    ? { ...row, ...patch, rate: null, total: null, totals: null, agreementLaborTotal: undefined,
      manualTotal: undefined, quoteCalculation: undefined, agreementCalculationKey: undefined, manualCalculation: false, manualRate: false, clientRatePending: true }
    : { ...row, ...patch };
}
